import React, { useState, useEffect } from 'react';
import { db, storage } from './firebase'; 
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default function DatosPersonal({ edificioActual }) {
  const coleccionPersonal = edificioActual ? `personal_${edificioActual.id}` : 'personal';

  const [personal, setPersonal] = useState([]);
  const [nombre, setNombre] = useState('');
  const [cargo, setCargo] = useState('');
  const [dni, setDni] = useState('');
  const [archivoFoto, setArchivoFoto] = useState(null);
  const [cargando, setCargando] = useState(false);

  // ✏️ Estados para controlar la edición
  const [editandoId, setEditandoId] = useState(null);
  const [fotoActualUrl, setFotoActualUrl] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, coleccionPersonal), (snapshot) => {
      const lista = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPersonal(lista);
    });
    return () => unsubscribe();
  }, [coleccionPersonal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre || !cargo || !dni) {
      alert('Por favor complete los campos obligatorios.');
      return;
    }

    setCargando(true);
    try {
      let urlFotoFinal = fotoActualUrl || 'https://via.placeholder.com/150';

      // Si seleccionaste una nueva fotografía, la subimos a Firebase Storage
      if (archivoFoto) {
        const storageRef = ref(storage, `personal_${edificioActual?.id || 'general'}/foto_${dni}_${Date.now()}_${archivoFoto.name}`);
        await uploadBytes(storageRef, archivoFoto);
        urlFotoFinal = await getDownloadURL(storageRef);
      }

      if (editandoId) {
        // 🔄 Actualizar registro existente en Firestore
        await updateDoc(doc(db, coleccionPersonal, editandoId), {
          nombre: nombre.trim(),
          cargo: cargo.trim(),
          dni: dni.trim(),
          fotoUrl: urlFotoFinal
        });
        alert('¡Colaborador actualizado con éxito!');
      } else {
        // ➕ Registrar nuevo colaborador
        await addDoc(collection(db, coleccionPersonal), {
          nombre: nombre.trim(),
          cargo: cargo.trim(),
          dni: dni.trim(),
          fotoUrl: urlFotoFinal,
          fechaRegistro: new Date().toLocaleDateString('es-PE')
        });
        alert('¡Colaborador registrado con éxito!');
      }

      // Limpiar formulario y salir del modo edición
      limpiarFormulario();
    } catch (error) {
      console.error('Error al guardar personal: ', error);
      alert('Hubo un error al guardar los datos.');
    } finally {
      setCargando(false);
    }
  };

  const iniciarEdicion = (p) => {
    setEditandoId(p.id);
    setNombre(p.nombre || '');
    setCargo(p.cargo || '');
    setDni(p.dni || '');
    setFotoActualUrl(p.fotoUrl || '');
    setArchivoFoto(null);
  };

  const limpiarFormulario = () => {
    setNombre('');
    setCargo('');
    setDni('');
    setArchivoFoto(null);
    setEditandoId(null);
    setFotoActualUrl('');
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este registro del personal?')) {
      try {
        await deleteDoc(doc(db, coleccionPersonal, id));
        if (editandoId === id) {
          limpiarFormulario();
        }
      } catch (error) {
        console.error('Error al eliminar:', error);
      }
    }
  };

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h2 style={{ color: '#0f172a', margin: '0 0 5px 0' }}>
            👷 Gestión de Personal {edificioActual ? `- Edificio ${edificioActual.nombre}` : ''}
          </h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
            Directorio y control del personal asignado al edificio.
          </p>
        </div>
        <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '8px 15px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.9rem' }}>
          Total Colaboradores: {personal.length}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px', alignItems: 'start' }}>
        
        {/* Formulario de Registro o Edición */}
        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
            <h3 style={{ color: '#1e293b', margin: 0, fontSize: '1.1rem' }}>
              {editandoId ? '✏️ Editar Colaborador' : '➕ Registrar Nuevo Colaborador'}
            </h3>
            {editandoId && (
              <button 
                type="button" 
                onClick={limpiarFormulario}
                style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}
              >
                Cancelar edición
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#334155' }}>Nombre Completo:</label>
              <input
                type="text"
                placeholder="Ej. Juan Pérez"
                value={nombre}
                onChange={e => setNombre(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1rem', boxSizing: 'border-box' }}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#334155' }}>Cargo / Puesto:</label>
              <input
                type="text"
                placeholder="Ej. Seguridad / Mantenimiento"
                value={cargo}
                onChange={e => setCargo(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1rem', boxSizing: 'border-box' }}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#334155' }}>DNI:</label>
              <input
                type="text"
                placeholder="Ej. 12345678"
                value={dni}
                onChange={e => setDni(e.target.value)}
                maxLength="8"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '1rem', boxSizing: 'border-box' }}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 'bold', display: 'block', marginBottom: '5px', color: '#334155' }}>Fotografía del Colaborador:</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => setArchivoFoto(e.target.files[0])}
                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', backgroundColor: 'white', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                {editandoId ? 'Déjalo vacío si deseas conservar la foto actual.' : 'Sube la foto que te hayan enviado (JPG o PNG).'}
              </span>
            </div>
            <button 
              type="submit" 
              disabled={cargando}
              style={{ backgroundColor: editandoId ? '#0284c7' : '#10b981', color: 'white', border: 'none', padding: '12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', marginTop: '5px' }}
            >
              {cargando ? 'Procesando...' : (editandoId ? 'Actualizar Colaborador 💾' : 'Guardar Colaborador 💾')}
            </button>
          </form>
        </div>

        {/* Listado de Personal */}
        <div>
          <h3 style={{ color: '#1e293b', marginTop: 0, marginBottom: '20px', fontSize: '1.1rem' }}>
            📋 Personal Activo Registrado
          </h3>
          {personal.length === 0 ? (
            <div style={{ backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#64748b' }}>
              No hay personal registrado en este edificio todavía.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
              {personal.map(p => (
                <div 
                  key={p.id} 
                  style={{ 
                    backgroundColor: '#ffffff', 
                    border: editandoId === p.id ? '2px solid #0284c7' : '1px solid #e2e8f0', 
                    borderRadius: '10px', 
                    padding: '15px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '15px', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)', 
                    position: 'relative' 
                  }}
                >
                  <img
                    src={p.fotoUrl}
                    alt={p.nombre}
                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #cbd5e1' }}
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/150'; }}
                  />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 4px 0', color: '#0f172a', fontSize: '1.05rem' }}>{p.nombre}</h4>
                    <p style={{ margin: '0 0 4px 0', color: '#0284c7', fontWeight: 'bold', fontSize: '0.85rem', textTransform: 'uppercase' }}>{p.cargo}</p>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>DNI: <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{p.dni}</span></p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => iniciarEdicion(p)}
                      style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.9rem' }}
                      title="Editar colaborador"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={() => handleEliminar(p.id)}
                      style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.9rem' }}
                      title="Eliminar colaborador"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}